// Keyword-targeted, static use-case landing pages for organic search.
// Each entry renders at /use-cases/:slug via UseCase.jsx.

export const USE_CASES = [
  {
    slug: "ai-product-ad-generator",
    icon: "ShoppingBag",
    title: "AI Product Ad Generator",
    h1: "The AI product ad generator for fast-moving brands",
    description:
      "Turn a product photo or prompt into scroll-stopping product ads in seconds. Generate on-brand images, videos and voiceovers with Nox AI — no designer, no production budget.",
    keywords: "AI product ad generator, product ad maker, AI product photography, ecommerce ad creator",
    intro:
      "Great product ads used to mean a photographer, a studio and days of turnaround. Nox AI does it in seconds. Drop in a product image or describe what you want, and get clean, high-resolution product shots, animated video ads and matching voiceovers — all ready to run on Meta, TikTok and Google.",
    bullets: [
      { title: "Studio-grade product shots", desc: "Place your product in any scene with FLUX, Ideogram and Bria Product Shot — no camera required." },
      { title: "Animate any product", desc: "Turn a still product image into a smooth motion ad with one click using image-to-video." },
      { title: "On-brand automatically", desc: "Save a brand kit and your logo and colors are composited onto every asset." },
    ],
    faqs: [
      { q: "Can I use my own product photo?", a: "Yes. Upload a product image and Nox AI can restyle it, drop it into new scenes, or animate it into a video ad." },
      { q: "What formats can I export?", a: "Square (1:1), landscape (16:9) and vertical (9:16) — everything you need for feeds, Reels, Shorts and YouTube." },
      { q: "Do I need design skills?", a: "No. If you can write a sentence describing your product, you can generate a finished ad." },
    ],
  },
  {
    slug: "ai-reels-maker",
    icon: "Clapperboard",
    title: "AI Reels & Shorts Maker",
    h1: "Make vertical Reels and Shorts with AI",
    description:
      "Create 9:16 Reels, TikToks and YouTube Shorts with AI. Generate vertical video, auto-reframe existing clips, add captions and background music — then export a finished MP4 with Nox AI.",
    keywords: "AI Reels maker, AI Shorts maker, vertical video generator, TikTok video maker AI, 9:16 video AI",
    intro:
      "Short-form video wins attention — but making it is slow. Nox AI generates vertical 9:16 video from a prompt, reframes your landscape clips for Reels and Shorts, burns in auto-captions for muted feeds, and lets you drop a music track on top. Assemble a finished, ready-to-post MP4 in minutes.",
    bullets: [
      { title: "Built for 9:16", desc: "Generate native vertical video, or AI-reframe any 16:9 clip to 9:16 for Reels and Shorts." },
      { title: "Auto-captions", desc: "Burn styled subtitles into your video automatically — most social video is watched on mute." },
      { title: "One-click assembly", desc: "Combine video, background music and captions into a single finished MP4." },
    ],
    faqs: [
      { q: "Can I turn a landscape video into a Reel?", a: "Yes. The Reframe tool uses AI to intelligently re-crop and fill 16:9 footage into a 9:16 vertical format." },
      { q: "Does it add captions?", a: "Yes. Auto-captions transcribe your video and burn styled subtitles straight onto the clip." },
      { q: "Can I add music?", a: "You can generate a track with AI or upload your own, then mix it in with the Assemble Ad tool." },
    ],
  },
  {
    slug: "ai-video-ad-maker",
    icon: "Video",
    title: "AI Video Ad Maker",
    h1: "Create video ads with AI in minutes",
    description:
      "Turn a line of copy into a polished video ad. Generate video with Kling, Veo and Grok, add an AI voiceover and music, then export a finished ad — no editor or production crew needed.",
    keywords: "AI video ad maker, AI video generator for ads, text to video ad, AI commercial maker",
    intro:
      "Nox AI turns an idea into a finished video ad. Describe your concept and generate motion with top models like Kling, Veo and Grok. Layer on a studio-quality AI voiceover, add background music, burn in captions, and assemble it all into one export-ready MP4 — the whole pipeline in one place.",
    bullets: [
      { title: "Text-to-video", desc: "Generate cinematic video ads from a prompt, or animate an existing product image." },
      { title: "Voice + music", desc: "Add narration with ElevenLabs voices and a generated soundtrack, mixed to the right levels." },
      { title: "Finished, not just clips", desc: "Assemble video, audio and captions into a single ready-to-run ad." },
    ],
    faqs: [
      { q: "Which video models can I use?", a: "Nox AI supports Kling, Veo and Grok for generation, plus editing tools for upscaling, restyling and reframing." },
      { q: "Can I add my own script as a voiceover?", a: "Yes. Type your script, pick a voice, and Nox AI generates the narration and syncs it to your video." },
      { q: "How long does it take?", a: "Image generation is near-instant; video renders in a few minutes depending on the model and length." },
    ],
  },
  {
    slug: "ai-voiceover-generator",
    icon: "Mic",
    title: "AI Voiceover Generator",
    h1: "AI voiceover generator for ads and video",
    description:
      "Generate studio-quality AI voiceovers for ads, explainers and video in seconds. Choose from natural ElevenLabs voices, then merge the narration straight onto your video with Nox AI.",
    keywords: "AI voiceover generator, text to speech for ads, AI narration, ElevenLabs voiceover, ad voiceover maker",
    intro:
      "Skip the recording booth. Nox AI turns your script into natural, studio-quality narration using ElevenLabs voices — then merges the voiceover directly onto your video in one click. Perfect for ads, product explainers, social videos and talking-avatar spokespeople.",
    bullets: [
      { title: "Natural voices", desc: "A range of realistic ElevenLabs voices for any tone — energetic, warm, authoritative." },
      { title: "Merge onto video", desc: "Drop the generated voiceover straight onto your clip without leaving the app." },
      { title: "Talking avatars", desc: "Pair a voiceover with a portrait to create an AI spokesperson that speaks your script." },
    ],
    faqs: [
      { q: "Can I use the voiceover on my video?", a: "Yes. Generate the narration and merge it onto any video, or use it to drive a talking avatar." },
      { q: "How natural do the voices sound?", a: "Nox AI uses ElevenLabs, one of the most realistic text-to-speech engines available." },
      { q: "What can I use it for?", a: "Ads, explainers, social content, YouTube intros, and AI spokesperson videos." },
    ],
  },
  {
    slug: "ai-talking-avatar-generator",
    icon: "UserSquare",
    title: "AI Talking Avatar Generator",
    h1: "Create an AI talking avatar from a photo",
    description:
      "Turn a portrait photo and a script into a talking spokesperson video. Nox AI generates the voiceover and lip-syncs it to your avatar automatically — no camera, no actor.",
    keywords: "AI talking avatar generator, AI spokesperson video, photo to talking video, AI presenter, UGC avatar maker",
    intro:
      "Create a spokesperson without a camera or an actor. Upload a portrait, type what they should say, and Nox AI generates the voiceover and lip-syncs it to your avatar — producing a natural talking-head video perfect for UGC-style ads, explainers and product intros.",
    bullets: [
      { title: "Photo to talking video", desc: "Any clear front-facing portrait becomes a talking presenter." },
      { title: "Your script, their voice", desc: "Type the script, pick a voice, and the audio is generated and lip-synced automatically." },
      { title: "Full-length, always", desc: "The video length matches your script, so the whole message plays start to finish." },
    ],
    faqs: [
      { q: "What kind of photo works best?", a: "A clear, well-lit, front-facing portrait gives the most natural result." },
      { q: "Do I need to record audio?", a: "No. Just type your script and Nox AI generates the voiceover and syncs the lips for you." },
      { q: "What's it good for?", a: "UGC-style ads, product explainers, spokesperson intros and social content." },
    ],
  },
  {
    slug: "ai-content-creation",
    icon: "PenLine",
    title: "AI Content Creation",
    h1: "Create content of every kind with AI",
    description:
      "One AI studio for all your content. Generate images, video, voiceovers, music and more — for social, marketing, YouTube, personal projects and anything you can imagine, with Nox AI.",
    keywords: "AI content creation, AI content generator, AI creative studio, make content with AI, all-in-one AI tool",
    intro:
      "Nox AI is your all-in-one creative studio. Whatever you're making — a social post, a video, a podcast intro, a piece of art or a full campaign — you can generate it here from a single prompt. Images, video, voiceovers, music, avatars and editing tools all live in one place, so you never have to juggle a dozen apps.",
    bullets: [
      { title: "Everything in one place", desc: "Images, video, voice, music, avatars and editing — one studio, no app-switching." },
      { title: "From idea to finished", desc: "Generate, edit, add sound and captions, then export a polished result in minutes." },
      { title: "For anyone", desc: "No design or editing skills needed — if you can describe it, you can create it." },
    ],
    faqs: [
      { q: "What kinds of content can I make?", a: "Images, videos, voiceovers, music, sound effects, talking avatars and fully assembled clips — for any purpose." },
      { q: "Do I need creative experience?", a: "No. Nox AI is built so anyone can create professional-looking content from a simple prompt." },
      { q: "Can I use it for personal projects?", a: "Absolutely — from social posts and gifts to hobby projects, not just business content." },
    ],
  },
  {
    slug: "ai-social-media",
    icon: "Share2",
    title: "AI for Social Media",
    h1: "Create scroll-stopping social content with AI",
    description:
      "Make social media content fast with AI. Generate posts, Reels, Shorts and TikToks, auto-reframe to 9:16, add captions and music, and keep a consistent look — all in one place with Nox AI.",
    keywords: "AI social media content, AI content for Instagram, TikTok content maker, social media post generator AI, Reels and Shorts AI",
    intro:
      "Feed the algorithm without burning out. Nox AI helps you create a steady stream of social content — eye-catching images, vertical videos, voiceovers and music — then reframe, caption and assemble it for Instagram, TikTok, YouTube and beyond. Post more, stress less.",
    bullets: [
      { title: "Every format", desc: "Square, vertical and landscape — generate or reframe content for any platform." },
      { title: "Caption-ready", desc: "Auto-burn subtitles so your videos land even when watched on mute." },
      { title: "Stay consistent", desc: "Save a look and reuse it so your feed always feels cohesive." },
    ],
    faqs: [
      { q: "Which platforms is this for?", a: "Instagram, TikTok, YouTube, Facebook, LinkedIn — anywhere you post images or short video." },
      { q: "Can I make vertical video?", a: "Yes. Generate native 9:16 video or reframe existing clips for Reels, Shorts and TikTok." },
      { q: "Does it help me post consistently?", a: "Yes — batch-generate content and keep a consistent style so your feed stays on-brand." },
    ],
  },
  {
    slug: "ai-youtube-toolkit",
    icon: "Youtube",
    title: "AI YouTube Toolkit",
    h1: "Everything you need to make YouTube videos with AI",
    description:
      "An AI toolkit for YouTubers: generate video, add studio-quality voiceovers and music, create thumbnails, auto-caption and assemble finished videos — all in one place with Nox AI.",
    keywords: "AI YouTube tools, AI video for YouTube, YouTube thumbnail generator AI, AI voiceover YouTube, YouTube content creator AI",
    intro:
      "Make better YouTube videos without a full production setup. Nox AI generates b-roll and video from prompts, adds natural AI narration and background music, creates eye-catching thumbnail images, and burns in captions — then assembles it all into a finished, upload-ready video.",
    bullets: [
      { title: "Video + b-roll", desc: "Generate cinematic clips and b-roll to fill out your edits." },
      { title: "Voice & music", desc: "Add studio-quality narration and a soundtrack without a mic or a license headache." },
      { title: "Thumbnails that pop", desc: "Generate scroll-stopping thumbnail images to lift your click-through rate." },
    ],
    faqs: [
      { q: "Can I make thumbnails?", a: "Yes. Generate high-resolution thumbnail images with real text using our image models." },
      { q: "Can it narrate my script?", a: "Yes — type your script, choose a voice, and get a natural voiceover to add to your video." },
      { q: "What about captions?", a: "Auto-captions transcribe and burn styled subtitles onto your video automatically." },
    ],
  },
  {
    slug: "ai-art-generator",
    icon: "Palette",
    title: "AI Art & Image Generator",
    h1: "Generate stunning AI art and images",
    description:
      "Create beautiful AI art and images from a prompt. Use top models like FLUX, Ideogram, Grok and Nano Banana for illustrations, concept art, posters, wallpapers and more with Nox AI.",
    keywords: "AI art generator, AI image generator, text to image AI, AI illustration, AI poster maker, FLUX Ideogram image generator",
    intro:
      "Turn your imagination into images. Nox AI gives you the best image models — FLUX, Ideogram (with real, legible text), Grok and Nano Banana Pro — to create illustrations, concept art, posters, wallpapers, avatars and anything else, in any style and aspect ratio.",
    bullets: [
      { title: "Best-in-class models", desc: "FLUX, Ideogram, Grok and Nano Banana Pro — pick the right engine for the look you want." },
      { title: "Any style", desc: "Photorealistic, illustrated, 3D, retro, anime and more — just describe it." },
      { title: "Edit & upscale", desc: "Refine, remove backgrounds, relight, vectorize to SVG and upscale for print." },
    ],
    faqs: [
      { q: "What can I create?", a: "Illustrations, concept art, posters, wallpapers, product images, avatars — any image you can describe." },
      { q: "Can it render text in images?", a: "Yes. Ideogram is included specifically for crisp, legible text in your designs." },
      { q: "Can I print my art?", a: "Yes — use the upscaler to boost resolution for high-quality prints." },
    ],
  },
  {
    slug: "nox-ai-vs-canva",
    icon: "Scale",
    title: "Nox AI vs Canva",
    h1: "Nox AI vs Canva: which is better for AI content?",
    description:
      "Nox AI vs Canva compared. Canva is a design editor with AI add-ons; Nox AI is a generative studio that creates images, video, voiceovers and finished ads from a prompt. See which fits your workflow.",
    keywords: "Nox AI vs Canva, Canva alternative, AI content generator vs Canva, Canva AI alternative",
    intro:
      "Canva is a brilliant drag-and-drop design editor with some AI features bolted on. Nox AI is built the other way round — generation first. Instead of arranging templates, you describe what you want and Nox AI produces the image, the video, the voiceover and the finished ad. If your bottleneck is creating original assets (not laying them out), a generative studio saves far more time.",
    bullets: [
      { title: "Generate, don't assemble", desc: "Type a prompt and get finished images, video ads and voiceovers — no template hunting or manual layout." },
      { title: "Real video + audio pipeline", desc: "Text-to-video with Kling/Veo, AI voiceovers, music and auto-captions assembled into one MP4 — beyond static design." },
      { title: "Ad-ready automation", desc: "Auto Ads, Site-to-Video and scheduled social posting turn a URL into running campaigns." },
    ],
    faqs: [
      { q: "Is Nox AI a Canva replacement?", a: "For generating original images, video and voiceovers it replaces a lot of manual design work. Canva still shines for hands-on layout and print templates — many teams use both." },
      { q: "Does Nox AI do video like Canva?", a: "Nox AI generates video from prompts and images with models like Kling and Veo, adds AI narration and music, and assembles a finished ad — a generative pipeline rather than a timeline editor." },
      { q: "Which is cheaper?", a: "Nox AI uses simple credits per generation, so you pay for what you create. Compare it to your Canva Pro seat if you mainly need original AI assets." },
    ],
  },
  {
    slug: "nox-ai-vs-adcreative",
    icon: "GitCompare",
    title: "Nox AI vs AdCreative.ai",
    h1: "Nox AI vs AdCreative.ai: a fairer ad creative stack",
    description:
      "Nox AI vs AdCreative.ai compared. AdCreative focuses on static ad variations and scoring; Nox AI generates images, real video ads, voiceovers and auto-posts them. See the difference for modern ad teams.",
    keywords: "Nox AI vs AdCreative, AdCreative alternative, AI ad generator comparison, AdCreative.ai alternative",
    intro:
      "AdCreative.ai is known for spinning out many static ad variations and predicting performance. Nox AI covers static ads too, but goes further into motion: text-to-video ads, AI voiceovers, talking avatars, Site-to-Video from a URL, and scheduled auto-posting. If your ad mix is increasingly video and UGC, a generative studio keeps you covered end to end.",
    bullets: [
      { title: "Static and video ads", desc: "Generate scroll-stopping image ads plus real video ads with voiceover, music and captions." },
      { title: "URL to campaign", desc: "Site-to-Video scans your website and turns it into a promo video; Auto Ads drafts full concepts." },
      { title: "Publish, not just produce", desc: "Schedule and auto-post creative to your social pages on a recurring cadence." },
    ],
    faqs: [
      { q: "Does Nox AI score creatives?", a: "Creative scoring is on our roadmap. Today Nox AI's edge is producing a much wider range of finished assets — especially video and voiceover — from a single prompt." },
      { q: "Can Nox AI make video ads?", a: "Yes. That's a core strength: text-to-video, image-to-video, AI voiceover, music, captions and one-click assembly into a finished MP4." },
      { q: "Is there a free way to try it?", a: "Yes — create an account and you get starter credits to generate your first ads before choosing a plan." },
    ],
  },
  {
    slug: "ai-ugc-ad-maker",
    icon: "Users",
    title: "AI UGC Ad Maker",
    h1: "Make UGC-style ads with AI",
    description:
      "Create UGC-style video ads with AI. Generate talking-avatar spokespeople, add AI voiceovers and captions, and assemble authentic-feeling creator content for TikTok and Reels with Nox AI.",
    keywords: "AI UGC ad maker, AI UGC generator, AI creator content, talking avatar ad maker, UGC video AI",
    intro:
      "User-generated-style content converts because it feels authentic — but sourcing creators is slow and expensive. Nox AI generates UGC-style ads with AI talking avatars that deliver your script, complete with a natural voiceover and burned-in captions. Pair it with product footage and assemble a scroll-native ad in minutes.",
    bullets: [
      { title: "Talking avatars", desc: "Turn a photo and a script into a lip-synced spokesperson using AI avatar generation." },
      { title: "Script to voice", desc: "Type your hook and Nox AI narrates it with lifelike ElevenLabs voices, synced to the video." },
      { title: "Native captions", desc: "Auto-burn styled subtitles so your UGC ad lands even on muted feeds." },
    ],
    faqs: [
      { q: "Do I need real creators?", a: "No. Nox AI generates talking-avatar presenters, so you can produce creator-style ads without hiring talent." },
      { q: "Can I use my own script?", a: "Yes. Write the script, choose a voice and avatar, and Nox AI produces the lip-synced delivery." },
      { q: "What platforms is this for?", a: "Vertical UGC ads for TikTok, Instagram Reels and YouTube Shorts, exported as ready-to-post 9:16 MP4s." },
    ],
  },
  {
    slug: "ai-image-generator",
    icon: "Image",
    title: "AI Image Generator",
    h1: "The AI image generator built for brands",
    description:
      "Generate high-quality images from text with FLUX, Ideogram, Grok and Nano Banana. Edit, inpaint, remove backgrounds, upscale and keep a consistent brand look — all in one AI image generator.",
    keywords: "AI image generator, text to image, AI art generator, FLUX image generator, Ideogram AI",
    intro:
      "Nox AI is a text-to-image generator with the models that matter — FLUX for realism, Ideogram for crisp in-image text, Grok and Nano Banana Pro for range. Beyond generation, you get a full editing suite: inpainting, object erase, reframe to any ad size, background removal, relight, upscale and reusable saved characters for consistent identity.",
    bullets: [
      { title: "Best-in-class models", desc: "Switch between FLUX, Ideogram, Grok and Nano Banana Pro for the right look every time." },
      { title: "Full editing suite", desc: "Inpaint, erase objects, reframe, relight, remove backgrounds and upscale 4x — no Photoshop needed." },
      { title: "Consistent characters", desc: "Save characters and products once and reuse them across every generation for a coherent brand." },
    ],
    faqs: [
      { q: "Can it render text in images?", a: "Yes. Ideogram is included specifically for clean, legible text — ideal for posters, thumbnails and ads." },
      { q: "Can I edit an image after generating?", a: "Yes. Use magic edit, inpainting, object eraser, reframe, relight and upscale directly in the editor." },
      { q: "How much does each image cost?", a: "Images use a small number of credits each, so you only pay for what you generate." },
    ],
  },
  {
    slug: "ai-music-generator",
    icon: "Music",
    title: "AI Music Generator",
    h1: "Generate background music with AI",
    description:
      "Create royalty-free background music and soundtracks with AI. Describe a mood and Nox AI generates a track you can drop straight into your videos, ads and Reels.",
    keywords: "AI music generator, AI background music, royalty free music AI, text to music, soundtrack generator",
    intro:
      "Every video needs the right soundtrack, but licensing music is a hassle. Nox AI generates original background music from a text description — set the mood, genre and energy, and get a track ready to mix into your ad or Reel. Combine it with AI voiceover and captions in the Assemble tool for a finished export.",
    bullets: [
      { title: "Mood to music", desc: "Describe the vibe — upbeat, cinematic, lo-fi — and generate a fitting original track." },
      { title: "Made for video", desc: "Drop your track straight into the Assemble Ad tool alongside video, voiceover and captions." },
      { title: "No licensing headaches", desc: "Generate the audio you need instead of hunting for cleared stock music." },
    ],
    faqs: [
      { q: "Can I use the music in ads?", a: "Yes — generate a track for the mood you need and mix it into your video ads and social clips." },
      { q: "Can I add narration too?", a: "Yes. Pair generated music with an AI voiceover and captions, then assemble a finished MP4." },
      { q: "What length can I get?", a: "Short-form tracks suited to social videos and ads; loop or trim them in assembly." },
    ],
  },
  {
    slug: "ai-photo-editor",
    icon: "Wand2",
    title: "AI Photo Editor",
    h1: "Edit photos with AI — inpaint, erase, reframe, restore",
    description:
      "A complete AI photo editor: brush-based inpainting, one-tap object removal, outpaint/reframe to any size, face restore, background removal, relight and 4x upscale — all in Nox AI.",
    keywords: "AI photo editor, AI inpainting, AI object remover, AI photo restore, background remover, AI upscaler",
    intro:
      "Nox AI's editor turns rough photos into polished assets without Photoshop. Brush a region and replace it with inpainting, remove an unwanted object in one tap, expand any image to a new ad size with outpainting, restore and sharpen faces, cut backgrounds, relight the scene and upscale 4x. A before/after slider lets you see exactly what changed.",
    bullets: [
      { title: "Inpaint & erase", desc: "Paint an area to replace it, or brush an unwanted object to remove it cleanly — no prompt needed to erase." },
      { title: "Reframe any size", desc: "Outpaint and reframe to 1:1, 9:16 or 16:9 so one photo becomes every ad placement." },
      { title: "Restore & upscale", desc: "Fix and sharpen faces, remove backgrounds, relight and upscale 4x for print-ready quality." },
    ],
    faqs: [
      { q: "Can I remove an object from a photo?", a: "Yes. The object eraser lets you brush over anything unwanted and fills the area in cleanly." },
      { q: "Can I change part of an image?", a: "Yes. Brush the region with the inpainting tool and describe what should appear there." },
      { q: "Can I resize for different platforms?", a: "Yes. Reframe expands your image to any aspect ratio without cropping the original." },
    ],
  },
  {
    slug: "website-to-video-ad",
    icon: "Globe",
    title: "Website to Video Ad",
    h1: "Turn your website into a video ad with AI",
    description:
      "Paste a URL and Nox AI turns your website into a promo video ad — it scans your page, pulls real product images, writes a script, animates the shots and assembles a finished MP4.",
    keywords: "website to video, URL to video ad, AI promo video from website, turn website into video, Site to Video AI",
    intro:
      "Nox AI's Site-to-Video reads your website, extracts your actual product images and messaging, writes a short punchy script, animates the shots into motion with image-to-video, adds a voiceover and stitches everything into a finished promo. Go from a URL to a ready-to-post video ad without a brief, a shoot or an editor.",
    bullets: [
      { title: "Just paste a URL", desc: "Nox AI scans your site, pulls real product images and drafts a script automatically." },
      { title: "Real footage, animated", desc: "Your product shots are animated with image-to-video and assembled into a promo." },
      { title: "Finished, not raw clips", desc: "Voiceover, music and captions are combined into one export-ready MP4." },
    ],
    faqs: [
      { q: "Does it use my real product images?", a: "Yes. Site-to-Video extracts images directly from your page so the ad features your actual products." },
      { q: "Can I pick the length or style?", a: "You choose the number of scenes and the models used; Nox AI handles scripting, animation and assembly." },
      { q: "What do I get at the end?", a: "A finished MP4 saved to your gallery, ready to download or auto-post to social." },
    ],
  },
  {
    slug: "ai-youtube-thumbnail-maker",
    icon: "Youtube",
    title: "AI YouTube Thumbnail Maker",
    h1: "Make click-worthy YouTube thumbnails with AI",
    description:
      "Design high-CTR YouTube thumbnails with AI. Generate bold backgrounds, add crisp readable text with Ideogram, swap backgrounds and reframe to 16:9 — all in Nox AI.",
    keywords: "AI YouTube thumbnail maker, AI thumbnail generator, YouTube thumbnail AI, clickbait thumbnail maker",
    intro:
      "Thumbnails decide who clicks. Nox AI generates eye-catching thumbnail backgrounds, renders crisp legible headline text with Ideogram, lets you cut and replace backgrounds, and reframes any image to a perfect 16:9. Iterate on variations fast and pick the one that pops in the feed.",
    bullets: [
      { title: "Readable text", desc: "Ideogram renders sharp, legible headline text right in the image — no separate design step." },
      { title: "Bold backgrounds", desc: "Generate dramatic, high-contrast scenes that stand out in search and suggested feeds." },
      { title: "Perfect 16:9", desc: "Reframe and outpaint any image to a clean 1280x720-friendly 16:9 thumbnail." },
    ],
    faqs: [
      { q: "Can it put my face on the thumbnail?", a: "Yes. Upload your photo, remove or replace the background, and composite it into a generated scene." },
      { q: "Can it write the title text?", a: "Yes. Ideogram is built for crisp in-image text, so your headline stays sharp and readable." },
      { q: "What size are the thumbnails?", a: "Generate and reframe to 16:9, ideal for YouTube's 1280x720 thumbnail spec." },
    ],
  },
];

export const getUseCase = (slug) => USE_CASES.find((u) => u.slug === slug);

// Recommended models per use case — surfaced on the landing pages so visitors
// know which engine fits their goal BEFORE signing up. tag = medium.
const USE_CASE_MODELS = {
  "ai-product-ad-generator": [
    { name: "Bria Product Shot", tag: "Image", why: "Drops your real product into any studio scene, keeping it pixel-accurate." },
    { name: "FLUX Pro", tag: "Image", why: "Crisp, high-resolution hero product renders from a prompt." },
    { name: "Kling 2.5 Turbo", tag: "Video", why: "Animates a still product photo into a smooth motion ad — best value." },
  ],
  "ai-reels-maker": [
    { name: "Grok Imagine", tag: "Video", why: "Generates native vertical clips with built-in sound." },
    { name: "Kling 2.5 Turbo", tag: "Video", why: "Fluid 9:16 motion at a low credit cost for high-volume posting." },
    { name: "ElevenLabs", tag: "Voice", why: "Natural narration to layer over your Reel." },
  ],
  "ai-video-ad-maker": [
    { name: "Veo 3", tag: "Video", why: "Premium, cinematic video ads with native audio." },
    { name: "Kling 2.5 Turbo", tag: "Video", why: "Great motion quality for a fraction of the cost — ideal for testing angles." },
    { name: "ElevenLabs", tag: "Voice", why: "Studio-grade ad narration synced to your cut." },
  ],
  "ai-voiceover-generator": [
    { name: "ElevenLabs", tag: "Voice", why: "The most natural, expressive text-to-speech voices available." },
    { name: "Kling AI Avatar", tag: "Avatar", why: "Turn that voiceover into a lip-synced on-screen presenter." },
  ],
  "ai-talking-avatar-generator": [
    { name: "Kling AI Avatar", tag: "Avatar", why: "Lip-syncs your script to any front-facing portrait." },
    { name: "ElevenLabs", tag: "Voice", why: "Generates the natural voice that drives the avatar." },
  ],
  "ai-content-creation": [
    { name: "FLUX / Nano Banana Pro", tag: "Image", why: "Versatile image generation for any style or format." },
    { name: "Kling 2.5 Turbo", tag: "Video", why: "Fast, affordable motion for social and ads." },
    { name: "ElevenLabs", tag: "Voice", why: "Add narration to any piece in one click." },
  ],
  "ai-social-media": [
    { name: "Grok Imagine", tag: "Video", why: "Vertical video with sound, made for feeds." },
    { name: "FLUX Schnell", tag: "Image", why: "Near-instant, cheap batch images for a steady posting cadence." },
    { name: "Kling 2.5 Turbo", tag: "Video", why: "Reliable 9:16 motion for Reels, Shorts and TikTok." },
  ],
  "ai-youtube-toolkit": [
    { name: "Veo 3", tag: "Video", why: "Cinematic b-roll and intro footage with audio." },
    { name: "Ideogram v3", tag: "Image", why: "Renders crisp, legible headline text for thumbnails." },
    { name: "ElevenLabs", tag: "Voice", why: "Narrate your script without a mic." },
  ],
  "ai-art-generator": [
    { name: "FLUX Pro", tag: "Image", why: "Best-in-class realism and detail." },
    { name: "Ideogram v3", tag: "Image", why: "Reliable, legible text inside your artwork." },
    { name: "Nano Banana Pro", tag: "Image", why: "Broad stylistic range for illustration and concept art." },
  ],
  "nox-ai-vs-canva": [
    { name: "Kling 2.5 Turbo", tag: "Video", why: "Real text-to-video — something Canva can't generate." },
    { name: "FLUX Pro", tag: "Image", why: "Original photoreal assets from a prompt, not templates." },
    { name: "ElevenLabs", tag: "Voice", why: "Built-in AI narration for finished videos." },
  ],
  "nox-ai-vs-adcreative": [
    { name: "Veo 3", tag: "Video", why: "Full video ads with audio, beyond static variations." },
    { name: "FLUX Pro", tag: "Image", why: "High-quality static ad creative." },
    { name: "Kling 2.5 Turbo", tag: "Video", why: "Cheap, fast motion for A/B testing hooks." },
  ],
  "ai-ugc-ad-maker": [
    { name: "Kling AI Avatar", tag: "Avatar", why: "Creator-style spokesperson from a single photo." },
    { name: "ElevenLabs", tag: "Voice", why: "Authentic-sounding hook narration." },
    { name: "Grok Imagine", tag: "Video", why: "Vertical UGC-native product footage with sound." },
  ],
  "ai-image-generator": [
    { name: "FLUX Pro", tag: "Image", why: "Top realism and prompt adherence." },
    { name: "Ideogram v3", tag: "Image", why: "Sharp, readable text in your images." },
    { name: "Nano Banana Pro", tag: "Image", why: "Great all-rounder for range and speed." },
  ],
  "ai-music-generator": [
    { name: "Nox Music", tag: "Audio", why: "Generates original, royalty-free background tracks from a mood description." },
    { name: "ElevenLabs", tag: "Voice", why: "Pair the track with narration for a finished mix." },
  ],
  "ai-photo-editor": [
    { name: "FLUX Fill", tag: "Editing", why: "Brush-based inpainting to replace any region." },
    { name: "Object Eraser", tag: "Editing", why: "One-tap clean removal of unwanted objects." },
    { name: "4x Upscaler", tag: "Editing", why: "Print-ready sharpening and resolution boost." },
  ],
  "website-to-video-ad": [
    { name: "Kling 2.5 Turbo", tag: "Video", why: "Animates your scraped product shots into a promo — best value." },
    { name: "Grok Imagine", tag: "Video", why: "Adds native sound to the generated scenes." },
    { name: "ElevenLabs", tag: "Voice", why: "Auto-narrates the script written from your site." },
  ],
  "ai-youtube-thumbnail-maker": [
    { name: "Ideogram v3", tag: "Image", why: "Renders bold, legible title text right in the thumbnail." },
    { name: "FLUX Pro", tag: "Image", why: "Dramatic, high-contrast backgrounds that pop in the feed." },
    { name: "Reframe / Outpaint", tag: "Editing", why: "Expands any image to a clean 16:9 thumbnail." },
  ],
};

USE_CASES.forEach((u) => {
  u.models = USE_CASE_MODELS[u.slug] || [];
});
